import { useCallback, useMemo, useRef } from 'react';
import I18n from 'I18n';
import { InlineLabel } from 'foundations-components/transitional/components/InlineLabel';
import UsersAndTeamsSelect from 'ui-asset-management-lib/components/tableFilters/UsersAndTeamsSelect';
import { getAllChildTeamIdsForParentTeamId } from 'ui-asset-management-lib/utils/TeamUtils';
import useFetchTeams from 'framework-listing-lib/internal/hooks/useFetchTeams';
import useCrmObjectTypeTracking from 'framework-listing-lib/internal/hooks/useCrmObjectTypeTracking';
import { SELECTION_ID_ALL, SELECTION_ID_MY_TEAMS, SELECTION_TYPE_CUSTOM, TEAMS_PROPERTY_NAME } from 'framework-listing-lib/constants/access';
import useCrmObjectTypeName from 'framework-listing-lib/internal/hooks/useCrmObjectTypeName';
import useUserInfo from 'framework-listing-lib/internal/hooks/useUserInfo';
import { HAS_PROPERTY, IN } from 'framework-listing-lib/utils/filters';
import ListingLibFilterWrapper from 'framework-listing-lib/filters/ListingLibFilterWrapper';
import SelectButtonContent from 'framework-listing-lib/internal/FilterBar/components/SelectButtonContent';
import { DEFAULT_USE, getMyTeamIds, getTeamIdsFromChangeEvent, getSelectedValueFromFilters, teamsAreFilterable, getAllTeamIds, isTeamOption, handleDeselectParentTeam, getMultiTeamAction, MULTI_TEAM_ACTIONS } from 'framework-listing-lib/utils/accessUtils';
import { useListingLibQuickFilters } from 'framework-listing-lib/hooks/useQuickFilters';
import { jsx as _jsx } from "react/jsx-runtime";
export default function ListingLibTeamsSelect({
  multi,
  viewAllAssetsScope,
  viewTeamOwnedAssetsScope,
  disabled,
  use = DEFAULT_USE
}) {
  const {
    data: teams,
    loading: loadingTeams,
    error: errorLoadingTeams
  } = useFetchTeams({
    viewAllAssetsScope,
    viewTeamOwnedAssetsScope
  });
  const {
    trackCrmTableInteraction
  } = useCrmObjectTypeTracking();
  const {
    getQuickFilterValue,
    quickFilters,
    removeQuickFilter,
    setQuickFilter
  } = useListingLibQuickFilters();

  /**
   * Preserve @multi value when set (true / false)
   * Otherwise:
   *   - make multi true when @use is button: https://git.hubteam.com/HubSpot/CampaignsUXTaskForce/issues/7
   */
  const isMulti = useMemo(() => {
    if (multi !== undefined) {
      return multi;
    }
    return use === 'button';
  }, [multi, use]);

  /**
   * Truthy when user selects "All teams"
   */
  const selectAllRef = useRef(false);
  /**
   * Truthy when user selects "My teams"
   */
  const selectMyTeamsRef = useRef(false);
  const {
    userInfo
  } = useUserInfo();
  const loadingUserInfo = !userInfo;
  const myTeamIds = useMemo(() => getMyTeamIds(userInfo), [userInfo]);
  const allTeamIds = useMemo(() => getAllTeamIds(teams), [teams]);
  const value = useMemo(() => {
    const selectedTeamIds = getQuickFilterValue(TEAMS_PROPERTY_NAME, []);
    const hasAllTeamsOption = quickFilters.some(filter => filter.operator === 'HAS_PROPERTY' && filter.property === TEAMS_PROPERTY_NAME);
    const result = getSelectedValueFromFilters({
      allTeams: teams,
      hasAllTeamsOption,
      isMulti,
      myTeamIds,
      selectedTeamIds
    });
    const resultAsArray = Array.isArray(result) ? result : [result];
    selectAllRef.current = resultAsArray.includes(SELECTION_ID_ALL);
    selectMyTeamsRef.current = resultAsArray.includes(SELECTION_ID_MY_TEAMS);
    return result;
  }, [getQuickFilterValue, isMulti, myTeamIds, quickFilters, teams]);
  const handleChange = useCallback(changeValue => {
    let teamIds = [];
    if (Array.isArray(changeValue)) {
      const newValue = handleDeselectParentTeam({
        prevValue: getQuickFilterValue(TEAMS_PROPERTY_NAME),
        newValue: changeValue,
        teams
      });
      const multiTeamAction = getMultiTeamAction({
        allTeamIds,
        myTeamIds,
        newValue,
        selectAllRefValue: selectAllRef.current,
        selectMyTeamsRefValue: selectMyTeamsRef.current
      });
      if (multiTeamAction === MULTI_TEAM_ACTIONS.SELECT_MY_TEAMS) {
        for (const teamId of myTeamIds) {
          teamIds = [...teamIds, teamId, ...getAllChildTeamIdsForParentTeamId(teamId, teams)];
        }
      } else if (multiTeamAction === MULTI_TEAM_ACTIONS.DESELECT) {
        /**
         * Automatically deselect all teams when the MyTeams option is deselected
         *
         * If MyTeams is selected, and then the user manually selects additional individual teams,
         * we automatically deselect the MyTeams option.
         * This means that when MyTeams is manually deselected,
         * we can be sure that ALL teamIds were part of "My teams", and can be deselected
         */
        teamIds = [];
      } else if (multiTeamAction === MULTI_TEAM_ACTIONS.SELECT_ALL_TEAMS) {
        // Setting @teamIds to undefined to apply the correct HAS_PROPERTY filter
        teamIds = undefined;
      } else if (multiTeamAction === MULTI_TEAM_ACTIONS.NONE) {
        /**
         * At this point, user is selecting individual team ids and we should only take into consideration "TEAM" options.
         * "My teams" and "All teams" options have been already handled in the conditions above.
         */
        for (const team of newValue) {
          if (!isTeamOption(team)) {
            continue;
          }
          const {
            id,
            type
          } = team;
          teamIds = [...teamIds, ...getTeamIdsFromChangeEvent(type, id, myTeamIds, teams)];
        }
      }
    } else {
      const {
        id,
        type
      } = changeValue;
      teamIds = getTeamIdsFromChangeEvent(type, id, myTeamIds, teams);
    }
    trackCrmTableInteraction({
      action: 'filter-objects-by-team'
    });
    if (teamIds === undefined) {
      setQuickFilter(HAS_PROPERTY(TEAMS_PROPERTY_NAME));
      return;
    }
    teamIds = Array.from(new Set(teamIds));
    if (teamIds.length) {
      setQuickFilter(IN(TEAMS_PROPERTY_NAME, teamIds));
    } else {
      removeQuickFilter(TEAMS_PROPERTY_NAME);
    }
  }, [allTeamIds, getQuickFilterValue, myTeamIds, removeQuickFilter, setQuickFilter, teams, trackCrmTableInteraction]);
  const handleClearTeams = useCallback(() => {
    selectAllRef.current = false;
    selectMyTeamsRef.current = false;
    removeQuickFilter(TEAMS_PROPERTY_NAME);
  }, [removeQuickFilter]);
  const shouldRender = useMemo(() => teamsAreFilterable(teams), [teams]);
  const ButtonContent = useCallback(() => {
    const count = isMulti && Array.isArray(value) ? value.filter(option => option !== SELECTION_ID_ALL && option !== SELECTION_ID_MY_TEAMS).length : 0;
    return /*#__PURE__*/_jsx(SelectButtonContent, {
      count: count,
      messageName: "frameworkListingLib.teamsSelect.teams",
      messageNameWithCount: "frameworkListingLib.teamsSelect.teamsWithCounts"
    });
  }, [isMulti, value]);
  const {
    objectTypeNameSingular
  } = useCrmObjectTypeName();
  if (!shouldRender || loadingTeams || loadingUserInfo) {
    return null;
  }
  const selectComponent = /*#__PURE__*/_jsx(UsersAndTeamsSelect, {
    additionalOptions: [{
      type: SELECTION_TYPE_CUSTOM,
      text: I18n.text('frameworkListingLib.teamsSelect.allTeams'),
      value: SELECTION_ID_ALL
    }],
    allowPrivate: false,
    assetTypeDisplayName: objectTypeNameSingular,
    customTeamOptionDisabled: myTeamIds.length === 0,
    disabled: disabled,
    "data-test-id": "crm-listing-teams-select",
    onChange: handleChange,
    requestsError: Boolean(errorLoadingTeams),
    requestsLoading: loadingTeams,
    showAny: false,
    showUnassigned: false,
    teams: teams,
    users: [],
    multi: isMulti
    // These props need to be passed down like this as the component
    // uses them in a rest operator and don't have types for them
    ,

    ButtonContent: use === 'button' ? ButtonContent : null,
    className: 'p-x-0',
    anchorType: isMulti ? 'button' : undefined,
    value
  });
  if (use === 'label') {
    return /*#__PURE__*/_jsx(InlineLabel, {
      label: I18n.text('frameworkListingLib.teamsSelect.label'),
      "data-test-id": "teams-select-inline-label",
      children: selectComponent
    });
  }
  return /*#__PURE__*/_jsx(ListingLibFilterWrapper, {
    active: isMulti && Array.isArray(value) ? value.length > 0 : value !== SELECTION_ID_ALL,
    onClearFilter: handleClearTeams,
    children: selectComponent
  });
}